
import { useState } from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import { useFormik } from 'formik'
import { renderLogo, renderLogoSize, toAbsoluteUrl } from '../../../../_metronic/helpers'
import { useAuth } from '../core/Auth'
import authService from '@/modules/auth/components/auth-service'
import Countdown from 'react-countdown';
import * as authHelper from '../core/AuthHelpers'
import { toast } from 'react-toastify';
import { HttpStatusCode } from 'axios'
import { t } from 'i18next'

const loginSchema = Yup.object().shape({
  username: Yup.string()
    .required('Lütfen kullanıcı adınızı giriniz.'),
  password: Yup.string().required('Lütfen şifrenizi giriniz.'),
})

const initialValues = {
  username: '',
  password: '',
}

const otpSchema = Yup.object().shape({
  otpCode: Yup.string().required('Lütfen otp kodunuzu giriniz.')
    .matches(/^\d+$/, 'Sadece rakam giriniz.')
    .min(6, 'OTP kodu en az 6 haneli olmalıdır.')
    .max(6, 'OTP kodu en fazla 6 haneli olmalıdır.')
})

const initialOtpValues = {
  otpCode: '',
}

export function Login() {
  const duration = 180;
  const [key, setKey] = useState(0);
  const [endDate, setEndDate] = useState(Date.now() + duration * 1000);
  const [loading, setLoading] = useState(false)
  const [otpLoading, setOtpLoading] = useState(false);
  const [openOtpScreen, setOtpScreen] = useState(false);
  const { saveAuth, twoFactorAuth } = useAuth()
  const [email, setEmail] = useState('');
  const [showReSendButton, setShowReSendButton] = useState(true);
  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      try {
        const auth = await authService.signIn(values.username, values.password);
        if (auth.hash) {
          if(auth.access_token == undefined || auth.access_token == ""){
          setLoading(false)
          setOtpScreen(true);
          setEmail(auth.email);
          await twoFactorAuth(auth);
          setEndDate(Date.now() + duration * 1000);
          }
          else{
            setOtpScreen(false); 
            await saveAuth(auth);
          }
        }
      } catch (error) {
        console.error(error)
        setStatus(t('wrong_username_or_password'))
        setSubmitting(false)
        setLoading(false)
      }
    },
  });

  const formikOtp = useFormik({
    initialValues: initialOtpValues,
    validationSchema: otpSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      const otpErrors = () => {
        setStatus(t('verification_code_is_wrong'))
        setSubmitting(false)
        setOtpLoading(false)
      };
      setOtpLoading(true)
      try {
        const hash = authHelper.getAuthHash();
        const auth = await authService.checkOtp(hash, values.otpCode);
        if (auth.access_token) {
          await saveAuth(auth);
        }
        else {
          otpErrors();
        }
      } catch (error) {
        console.error(error)
        otpErrors();
      }
    },
  })

  const handleReSendOtpCode = async () => {
    formikOtp.resetForm();
    const hash = authHelper.getAuthHash();
    const reSendResponse = await authService.reSendOtp(hash);
    console.log(reSendResponse);
    if (reSendResponse.status == HttpStatusCode.Ok) {

      setShowReSendButton(true);
      resetCounter();
      toast.success(t('otp_code_has_been_sent'))
    }
    else if (reSendResponse.status == HttpStatusCode.TooManyRequests) {
      toast.warn(t('you_cannot_send_new_otp_code_please_wait'))
    }
    else if (reSendResponse.status == HttpStatusCode.RequestTimeout) {
      setOtpScreen(false);
    }
  }
  const resetCounter = async () => {
    setKey(prevKey => prevKey + 1); // Key'i değiştirerek sayacı sıfırla
    setEndDate(Date.now() + duration * 1000);
  }

  const logOut = async () => {
    formikOtp.resetForm();
    setOtpScreen(false);
  }

  return (
    <div>
      {
        !openOtpScreen ?
          <form
            className='form w-100'
            onSubmit={formik.handleSubmit}
            noValidate
            id='kt_login_signin_form'
          >
            <div className='d-flex flex-column flex-center py-15 px-5 px-md-15 w-100'>
              <Link to='/' className='mb-12'>
                <img alt='Logo' src={renderLogo()} className={renderLogoSize()} />
              </Link>
            </div>
            <div className='text-center mb-11'>
              <h1 className='text-gray-900 fw-bolder mb-3'>{t('login')}</h1>
            </div>

            {formik.status ? (
              <div className='mb-lg-5 alert alert-danger'>
                <div className='alert-text font-weight-bold'>{formik.status}</div>
              </div>
            ) : (<div className='mb-lg-20'></div>)}
            <div className='fv-row mb-8'>
              <input
                placeholder={t('username')}
                {...formik.getFieldProps('username')}
                className={clsx(
                  'form-control bg-transparent',
                  { 'is-invalid': formik.touched.username && formik.errors.username },
                  {
                    'is-valid': formik.touched.username && !formik.errors.username,
                  }
                )}
                type='text'
                name='username'
                autoComplete='off'
              />
            </div>
            <div className='fv-row mb-3'>
              <input
                type='password'
                placeholder={t('password')}
                autoComplete='off'
                {...formik.getFieldProps('password')}
                className={clsx(
                  'form-control bg-transparent',
                  {
                    'is-invalid': formik.touched.password && formik.errors.password,
                  },
                  {
                    'is-valid': formik.touched.password && !formik.errors.password,
                  }
                )}
              />
            </div>

            <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'>
              <div />
            </div>

            <div className='d-grid mb-10'>
              <button
                type='submit'
                id='kt_sign_in_submit'
                className='btn btn-primary'
                disabled={formik.isSubmitting || !formik.isValid}
              >
                {!loading && <span className='indicator-label'>{t('continue')}</span>}
                {loading && (
                  <span className='indicator-progress' style={{ display: 'block' }}>
                    {t('please_wait')}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>
            </div>
            <div className='text-gray-500 text-center fw-semibold fs-6'>
              {t('for_new_membership')} <a href='https://obase.com/iletisim/bize-ulasin' className='link-primary'>{t('contact_us')}</a>
            </div>
          </form>

          :

          <form
            className='form w-100'
            onSubmit={formikOtp.handleSubmit}
            noValidate
            id='kt_login_signin_form'
          >
            <div className='d-flex flex-column flex-center py-15 px-5 px-md-15 w-100'>
              <Link to='/' className='mb-12'>
                <img alt='Logo' src={renderLogo()} className={renderLogoSize()} />
              </Link>
            </div>
            <div className='text-center mb-11'>
            <h1 className='text-gray-900 fw-bolder mb-3'>{t('approval_code_has_been_sent')} : {email}</h1>
              <h1 className='text-gray-900 fw-bolder mb-3'><Countdown
                key={key}
                date={endDate}
                intervalDelay={1000}
                precision={0}
                renderer={props => <div>{props.total / 1000} {t('seconds_left')}</div>}
                onComplete={() => setShowReSendButton(false)}

              />
              </h1>
            </div>
            {formikOtp.status ? (
              <div className='mb-lg-20 alert alert-danger'>
                <div className='alert-text font-weight-bold'>{formikOtp.status}</div>
              </div>
            ) : (<div className='mb-lg-20'></div>)}
            <div className="fv-row mb-3 position-relative">
              {/* Tekrar Gönder Butonu */}
              <button
                type="button"
                disabled={showReSendButton}
                className="btn btn-primary position-absolute top-0 start-50 translate-middle-x"
                style={{
                  marginTop: "-50px",
                  width: "300px",
                }}
                onClick={handleReSendOtpCode}
              >
                {t("send_again_otp_code")}
              </button>
              <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'></div>
              {/* Input */}
              <input
                type="text"
                placeholder={t('verification_code')}
                autoComplete="off"
                {...formikOtp.getFieldProps("otpCode")}
                className={clsx(
                  "form-control bg-transparent mt-4",
                  {
                    "is-invalid": formikOtp.touched.otpCode && formikOtp.errors.otpCode,
                  },
                  {
                    "is-valid": formikOtp.touched.otpCode && !formikOtp.errors.otpCode,
                  }
                )}
              />
            </div>
            <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'></div>
            <div className='d-grid mb-10'>
              <button
                type='submit'
                id='kt_sign_in_submit'
                className='btn btn-primary'
                disabled={formikOtp.isSubmitting || !formikOtp.isValid}
              >
                {!otpLoading && <span className='indicator-label'>{t('continue')}</span>}
                {otpLoading && (
                  <span className='indicator-progress' style={{ display: 'block' }}>
                    {t('login')}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>
              <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'></div>
              <button
                type='button'
                id='kt_logout'
                className='btn btn-primary'
                onClick={logOut}
              >
                {!otpLoading && <span className='indicator-label'>{t('logout')}</span>}
              </button>
            </div>
          </form>
      }
    </div>
  )
}
